import { useEffect, useRef, useState } from "react";
import { StatusBadge } from "@/components/StatusBadge";

function FitValue({ value, unit }) {
  const boxRef = useRef(null);
  const textRef = useRef(null);
  const [scale, setScale] = useState(1);
  const isText = typeof value === "string" && !/^[\d₹$€%.,\s\-–—+\/x×:hminCr→]*$/.test(value);

  useEffect(() => {
    if (isText) return;
    const fit = () => {
      const box = boxRef.current, txt = textRef.current;
      if (!box || !txt) return;
      const avail = box.clientWidth;
      const need = txt.scrollWidth / (parseFloat(txt.dataset.scale) || 1);
      if (!avail || !need) return;
      const s = Math.max(0.45, Math.min(1, (avail / need) * 0.95));
      txt.dataset.scale = s;
      setScale(s);
    };
    fit();
    const ro = new ResizeObserver(fit);
    if (boxRef.current) ro.observe(boxRef.current);
    return () => ro.disconnect();
  }, [value, unit, isText]);

  const str = value == null ? "" : String(value);
  return (
    <div ref={boxRef} className="min-w-0 w-full h-10 sm:h-11 flex items-end" title={str}>
      {isText ? (
        <span className="font-mono font-bold tracking-tight text-lg sm:text-xl leading-none truncate w-full">{value}</span>
      ) : (
        <span ref={textRef} className="font-mono font-bold tracking-tight leading-none whitespace-nowrap tabular-nums inline-flex items-baseline gap-1.5"
          style={{ fontSize: `calc(2.125rem * ${scale})` }}>
          {value}
          {unit && <span className="text-slate-500 font-sans font-normal" style={{ fontSize: "0.875rem" }}>{unit}</span>}
        </span>
      )}
    </div>
  );
}

export function KpiCard({ title, value, unit, sub, status, testId, delay = 0, mtd }) {
  return (
    <div data-testid={testId} className={`pp-card relative p-4 sm:p-5 flex flex-col gap-2 min-w-0 h-full rise rise-${delay} hover:border-slate-300 transition-colors duration-200 ${status === "red" ? "border-rose-500/40" : ""}`}>
      <div className="flex items-start justify-between gap-2 min-h-5">
        <div className="pp-label leading-5 min-w-0 break-words" title={typeof title === "string" ? title : undefined}>{title}</div>
        {status && <div className="shrink-0 -mt-0.5 -mr-1"><StatusBadge status={status} /></div>}
      </div>
      <FitValue value={value} unit={unit} />
      {mtd !== undefined && <div className="flex items-baseline gap-1.5 text-xs leading-4 h-4 truncate" data-testid={testId ? `${testId}-mtd` : undefined} title={typeof mtd === "string" ? mtd : undefined}><span className="rounded bg-blue-50 px-1 text-[10px] font-bold tracking-wide text-blue-700">MTD</span><span className="font-mono font-semibold text-slate-800 tabular-nums">{mtd ?? "—"}</span></div>}
      <div className="text-xs text-slate-500 leading-4 h-4 truncate" title={typeof sub === "string" ? sub : undefined}>{sub || "\u00a0"}</div>
    </div>
  );
}
